"use client";

import { useState } from "react";
import { MessageTimestamp } from "./MessageTimestamp";

/**
 * 사용자 메시지 하단에 붙는 액션 바 — 복사·편집 버튼.
 */
export function UserMessageActions({
  text,
  createdAt,
  visible = false,
  onEdit,
  canEdit = true,
}: {
  text: string;
  createdAt?: string | Date;
  visible?: boolean;
  onEdit: () => void;
  canEdit?: boolean;
}) {
  const [copied, setCopied] = useState(false);

  const handleCopy = async () => {
    const payload = text || '';
    if (!payload) return;
    try {
      if (navigator.clipboard?.writeText) {
        await navigator.clipboard.writeText(payload);
      } else {
        const ta = document.createElement('textarea');
        ta.value = payload;
        ta.style.position = 'fixed';
        ta.style.opacity = '0';
        document.body.appendChild(ta);
        ta.select();
        document.execCommand('copy');
        document.body.removeChild(ta);
      }
      setCopied(true);
      setTimeout(() => setCopied(false), 1500);
    } catch (e) {
      console.error('[copy-user]', e);
    }
  };

  return (
    <div
      role="group"
      aria-label="프롬프트 액션"
      className={`flex items-center gap-0.5 transition-opacity duration-200 ${visible ? 'visible pointer-events-auto opacity-100' : 'invisible pointer-events-none opacity-0'}`}
    >
      <MessageTimestamp createdAt={createdAt} />
      <button
        type="button"
        onClick={handleCopy}
        aria-label={copied ? '복사됨' : '프롬프트 복사'}
        title={copied ? '복사됨' : '프롬프트 복사'}
        className="flex items-center justify-center h-8 w-8 cursor-pointer rounded-lg text-gray-500 dark:text-gray-400 hover:text-gray-800 dark:hover:text-gray-100 transition-colors"
      >
        {copied ? (
          <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.2" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
            <polyline points="20 6 9 17 4 12" />
          </svg>
        ) : (
          <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
            <rect x="9" y="9" width="13" height="13" rx="2" ry="2" />
            <path d="M5 15H4a2 2 0 0 1-2-2V4a2 2 0 0 1 2-2h9a2 2 0 0 1 2 2v1" />
          </svg>
        )}
      </button>
      {canEdit && (
        <button
          type="button"
          onClick={onEdit}
          aria-label="프롬프트 편집"
          title="프롬프트 편집"
          className="flex items-center justify-center h-8 w-8 cursor-pointer rounded-lg text-gray-500 dark:text-gray-400 hover:text-gray-800 dark:hover:text-gray-100 transition-colors"
        >
          <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
            <path d="M11 4H4a2 2 0 0 0-2 2v14a2 2 0 0 0 2 2h14a2 2 0 0 0 2-2v-7" />
            <path d="M18.5 2.5a2.121 2.121 0 0 1 3 3L12 15l-4 1 1-4 9.5-9.5z" />
          </svg>
        </button>
      )}
    </div>
  );
}

/**
 * 사용자 메시지 인라인 편집기 — 편집 모드 진입 시 bubble 자리에 렌더링.
 */
export function UserMessageEditor({
  initialText,
  onSave,
  onCancel,
}: {
  initialText: string;
  onSave: (newText: string) => void;
  onCancel: () => void;
}) {
  const [text, setText] = useState(initialText);
  const canSave = text.trim().length > 0 && text.trim() !== initialText.trim();

  return (
    <div className="w-full bg-gray-100 dark:bg-[#1f1f22] rounded-3xl border border-transparent dark:border-[#2a2a2e] shadow-sm p-3 flex flex-col gap-2">
      <textarea
        autoFocus
        value={text}
        onChange={e => setText(e.target.value)}
        onKeyDown={e => {
          if (e.key === 'Enter' && (e.metaKey || e.ctrlKey)) {
            e.preventDefault();
            if (canSave) onSave(text.trim());
          } else if (e.key === 'Escape') {
            e.preventDefault();
            onCancel();
          }
        }}
        rows={Math.max(2, Math.min(10, text.split('\n').length))}
        className="w-full resize-none bg-transparent text-[15px] leading-relaxed font-medium text-gray-900 dark:text-gray-100 outline-none px-3 py-1.5"
      />
      <div className="flex items-center justify-end gap-2">
        <button
          type="button"
          onClick={onCancel}
          className="px-4 py-1.5 rounded-full text-[12px] font-bold text-gray-600 dark:text-gray-400 hover:bg-gray-200 dark:hover:bg-[#2a2a2e] transition-colors"
        >
          취소
        </button>
        <button
          type="button"
          onClick={() => canSave && onSave(text.trim())}
          disabled={!canSave}
          className="px-4 py-1.5 rounded-full text-[12px] font-bold bg-gray-900 dark:bg-white text-white dark:text-gray-900 hover:bg-black dark:hover:bg-gray-200 transition-colors disabled:opacity-40 disabled:cursor-not-allowed"
        >
          업데이트
        </button>
      </div>
    </div>
  );
}
